import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import connect from '../utils/connect';
import combineActions from '../utils/combineActions';
import { confirmed, underReview } from '../constants/clientConfig';
import sportsApi from '../utils/sportsApi';

import { Button, Row, Col, LoadingDots } from '@app/ui';
import Tooltip from '@app/ui/sports-components/Tooltip';
import Checkbox from '@app/ui/sports-components/Checkbox';

import GetStartedPage from './GetStartedPage';
import getClientOnboarding from '../actions/settings/getClientOnboarding';

import RHCard from './RHCard';
import PlacementsSponsors from './PlacementsSponsors';
import './Onboarding.scss';
import { Context } from './contexts/OnboardingContext';

const Onboarding = ({
    client: { id: clientId },
    user,
    settings: {
        client: {
            onboarding = {
                rightsholders: [],
            },
        },
    },
}) => {
    const { t } = useTranslation('settings');
    const {
        hasSubmitted,
        setSubmit,
        isRemovingSP,
        configStatus,
        setConfigStatus,
        canSubmit,
        setSubmittable,
        isPlacementModalOpen,
        structureLocked,
        rhId,
        setRhId,
        hideInactiveAssets,
        setHideInactiveAssets,
    } = useContext(Context);

    const [isStarting, setIsStarting] = useState(() => {
        const id = `${user.id}-${clientId}`;
        return !localStorage.getItem(`mvc-onboarding-started--${id}`);
    });

    const { rightsholders } = onboarding;

    useEffect(() => {
        if (rhId) return;
        if (rightsholders.length > 0) {
            setRhId(rightsholders[0].id);
        }
    }, [rightsholders]);

    useEffect(() => {
        if (!rightsholders || !rightsholders.length) return;
        const newCanSubmit = rightsholders.some(r => {
            const { placements } = r;
            if (!placements.length) return false;
            return placements.some(p => p && p.sponsors.length > 0);
        });
        setSubmittable(newCanSubmit);
    }, [rightsholders]);

    const submit = async () => {
        try {
            const confirmation = confirm(
                t('clientConfiguration.onboarding.submitConfirm'),
            );
            if (!confirmation) return;

            const payload = {
                action: 'requestReview',
            };

            await sportsApi
                .url(`/clients/${clientId}/rsp-configuration`)
                .json(payload)
                .post();

            setSubmit(true);
            setConfigStatus(underReview);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    };

    const setStarted = () => {
        const id = `${user.id}-${clientId}`;
        localStorage.setItem(`mvc-onboarding-started--${id}`, 'true');
        setIsStarting(false);
    };

    const onToggleHideInactiveAssets = () => {
        setHideInactiveAssets(!hideInactiveAssets);
    };

    if (isStarting) {
        return <GetStartedPage startOnboarding={setStarted} />;
    }

    const isConfirmed = configStatus == confirmed;
    const isUnderReview = configStatus == underReview;

    return (
        <>
            {hasSubmitted && (
                <Row gutter={16}>
                    <Col
                        md={24}
                        className="gds-flex gds-flex--justify-between gds-flex--align-center "
                    >
                        <div
                            className="gds-container -color-bg-white -m-b-3"
                            style={{
                                boxShadow: `inset 0 -0.125rem 0 0 var(--primary), \
                                    inset 0 0 0 1px var(--primary), 0 0 0px 1px rgba(26,202,188,0), \
                                    0 0 0px 0 transparent`,
                                border: 'none',
                                width: '100%',
                            }}
                        >
                            <div className="gds-flex gds-flex--align-center">
                                <div>
                                    <i
                                        className="fal fa-exclamation-circle -p-l-2 -p-r-3 -color-tx-pri"
                                        style={{ fontSize: '1.25rem' }}
                                    />
                                </div>
                                {isUnderReview && (
                                    <p
                                        className="gds-text--body-sm"
                                        style={{ flex: '1 1 0%' }}
                                    >
                                        <Trans
                                            i18nKey="clientConfiguration.onboarding.submittedMessage"
                                            t={t}
                                            values={{
                                                contactSupport: t(
                                                    'clientConfiguration.onboarding.contactSupport',
                                                ),
                                            }}
                                            components={{
                                                link: (
                                                    <a
                                                        className="-m-h-1 -color-tx-pri"
                                                        href="https://relometrics.zendesk.com/hc/en-us/requests/new"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    />
                                                ),
                                            }}
                                        />
                                    </p>
                                )}
                                {isConfirmed && (
                                    <p
                                        className="gds-text--body-sm"
                                        style={{ flex: '1 1 0%' }}
                                    >
                                        {t(
                                            'clientConfiguration.onboarding.approvedMessage',
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            )}
            {Array.isArray(rightsholders) && rightsholders.length > 0 && (
                <Row gutter={16}>
                    <Col
                        md={24}
                        className="gds-flex gds-flex--justify-between gds-flex--align-center "
                    >
                        <div
                            className="onboarding gds-flex gds-flex--justify-start -overflow-x-auto"
                            style={{ flexWrap: 'nowrap', width: '100%' }}
                        >
                            {rightsholders
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(rh => (
                                    <RHCard
                                        rightsholder={rh}
                                        currentRightsholderId={rhId}
                                        selectRightsholder={setRhId}
                                        key={rh.id}
                                    />
                                ))}
                        </div>
                    </Col>
                </Row>
            )}

            <PlacementsSponsors />

            <div className="gds-flex gds-flex--align-center gds-flex--justify-between -m-v-2">
                <div className="gds-flex__item gds-flex__item--grow-0 -m-t-3">
                    <Checkbox
                        inline
                        label={
                            <>
                                {t(
                                    'clientConfiguration.onboarding.hideInactiveAssets',
                                )}
                                <Tooltip
                                    text={t(
                                        'clientConfiguration.onboarding.hideInactiveAssetsTooltip',
                                    )}
                                    position="top-right"
                                >
                                    <i className="fas fa-info-circle -m-h-1" />
                                </Tooltip>
                            </>
                        }
                        size="sm"
                        checked={hideInactiveAssets}
                        onChange={onToggleHideInactiveAssets}
                    />
                </div>
                {isRemovingSP && (
                    <div className="gds-flex__item gds-flex__item--grow-0 -m-t-3">
                        <LoadingDots />
                    </div>
                )}
                <div className="gds-flex__item gds-flex__item--grow-0 -m-t-3">
                    <Tooltip
                        position="top-left"
                        style={{ width: '100%' }}
                        text={
                            structureLocked
                                ? t(
                                      'clientConfiguration.onboarding.submitTooltip',
                                  )
                                : ''
                        }
                    >
                        <Button
                            size="small"
                            variant="primary"
                            style={{ width: '100%' }}
                            disabled={
                                !canSubmit ||
                                structureLocked ||
                                isPlacementModalOpen
                            }
                            onClick={submit}
                        >
                            {t(
                                'clientConfiguration.onboarding.submitConfiguration',
                            )}
                        </Button>
                    </Tooltip>
                </div>
            </div>
        </>
    );
};

Onboarding.propTypes = {
    client: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired,
};

export default connect(
    ['client', 'user', 'settings'],
    combineActions({
        getClientOnboarding,
    }),
)(Onboarding);
